import { useState } from 'react'
import { Modal } from '@/components/Modal'
import type { ConcreteOverridableField } from '../overrideUtils'

export interface AddOverrideModalProps {
  open: boolean
  onClose: () => void
  /** Allow-listed fields not yet overridden for the current firm, already
   * scoped to what applies to it (filterFieldsForFirm) - never a free-text
   * path, structurally enforcing PRD 06 AC2, and never an irrelevant
   * product/document type the firm doesn't actually use. */
  availableFields: ConcreteOverridableField[]
  onSelect: (field: ConcreteOverridableField) => void
}

interface FieldGroup {
  entityLabel: string
  fields: ConcreteOverridableField[]
}

// Groups by `${block}:${entityId}` via a Map, which preserves first-seen
// order regardless of how the underlying fields are interleaved
// (expandAllowlistToConcreteFields processes one allowlist pattern at a time
// across all entities, so a product's own fields aren't necessarily
// contiguous in the input) - groups come out product-then-document-type,
// fields within a group in governance's own allowlist_paths order.
function groupFields(fields: ConcreteOverridableField[]): FieldGroup[] {
  const groups = new Map<string, FieldGroup>()
  for (const field of fields) {
    const key = `${field.block}:${field.entityId}`
    const group = groups.get(key)
    if (group) group.fields.push(field)
    else groups.set(key, { entityLabel: field.entityLabel, fields: [field] })
  }
  return Array.from(groups.values())
}

// Search + grouped-list picker, mirroring product-types' AddDocumentTypeModal
// pattern but single-select: clicking a row adds it and closes immediately,
// since this is a picker (one field at a time), not a multi-select-then-
// confirm flow. Grouped by product/document type with a header per group -
// a flat list isn't navigable once document-type fields are in the mix (a
// single document type can have 30+ overridable fields).
export function AddOverrideModal({ open, onClose, availableFields, onSelect }: AddOverrideModalProps) {
  const [search, setSearch] = useState('')

  const query = search.trim().toLowerCase()
  const filtered = availableFields.filter((field) =>
    `${field.entityLabel} ${field.fieldLabel}`.toLowerCase().includes(query),
  )
  const groups = groupFields(filtered)

  function handleClose() {
    setSearch('')
    onClose()
  }

  return (
    <Modal open={open} title="Add Override" onClose={handleClose}>
      <div className="flex flex-col gap-3">
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search overridable fields…"
          autoFocus
          className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none placeholder:text-text-disabled"
        />
        <div className="flex max-h-[320px] flex-col gap-4 overflow-y-auto">
          {groups.length === 0 ? (
            <span className="text-meta text-text-disabled">No matching overridable fields</span>
          ) : (
            groups.map((group) => (
              <div key={group.entityLabel} className="flex flex-col gap-1">
                <span className="px-2 text-meta font-semibold uppercase tracking-wide text-text-secondary">
                  {group.entityLabel}
                </span>
                {group.fields.map((field) => (
                  <button
                    key={field.canonicalPath}
                    type="button"
                    onClick={() => {
                      onSelect(field)
                      handleClose()
                    }}
                    className="flex flex-col items-start gap-0.5 rounded-control px-2 py-1.5 text-left hover:bg-surface"
                  >
                    <span className="text-body text-text-primary">{field.fieldLabel}</span>
                    <span className="text-meta text-text-secondary">
                      Platform default: {String(field.platformDefaultValue ?? '-')}
                    </span>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  )
}
